import React, { useState, useEffect } from 'react';
import { 
    Target, 
    Plus, 
    TrendingUp, 
    Calendar, 
    Award, 
    Clock, 
    CheckCircle, 
    AlertTriangle,
    Edit,
    Trash2,
    BarChart3,
    Users,
    BriefcaseIcon,
    DollarSign
} from 'lucide-react';
import { API_BASE_URL } from "../config/apiconfig";

const GoalsSection = ({ user, profileData }) => {
    const [goals, setGoals] = useState([]);
    const [goalStats, setGoalStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [filter, setFilter] = useState('all');

    const [newGoal, setNewGoal] = useState({
        goal_type: 'hires',
        title: '',
        description: '',
        target_value: '',
        unit: 'candidates',
        period_type: 'monthly',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        priority: 'medium'
    });

    useEffect(() => {
        if (user?.id) {
            fetchGoals();
            fetchGoalStats();
        }
    }, [user?.id]);

    const fetchGoals = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/goals/recruiter/${user.id}`);
            const data = await response.json();
            if (data.success) {
                setGoals(data.goals);
            }
        } catch (error) {
            console.error('Error fetching goals:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchGoalStats = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/goals/stats/${user.id}`);
            const data = await response.json();
            if (data.success) {
                setGoalStats(data.stats);
            }
        } catch (error) {
            console.error('Error fetching goal stats:', error);
        }
    };

    const handleCreateGoal = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/goals`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...newGoal,
                    recruiter_id: user.id,
                    company_id: profileData.company_id
                })
            });

            const data = await response.json();
            if (data.success) {
                fetchGoals();
                fetchGoalStats();
                setShowCreateModal(false);
                setNewGoal({
                    goal_type: 'hires',
                    title: '',
                    description: '',
                    target_value: '',
                    unit: 'candidates',
                    period_type: 'monthly',
                    start_date: new Date().toISOString().split('T')[0],
                    end_date: '',
                    priority: 'medium'
                });
            }
        } catch (error) {
            console.error('Error creating goal:', error);
        }
    };

    const handleUpdateProgress = async (goalId, newValue) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/goals/${goalId}/progress`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    new_value: newValue,
                    change_reason: 'Manual update',
                    recorded_by: user.id
                })
            });

            if (response.ok) {
                fetchGoals();
                fetchGoalStats();
            }
        } catch (error) {
            console.error('Error updating progress:', error);
        }
    };

    const handleAutoCalculate = async (goalId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/goals/${goalId}/auto-calculate`, {
                method: 'POST'
            });

            if (response.ok) {
                fetchGoals();
                fetchGoalStats();
            }
        } catch (error) {
            console.error('Error auto-calculating:', error);
        }
    };

    const getGoalIcon = (goalType) => {
        switch (goalType) {
            case 'hires': return <Users className="w-5 h-5" />;
            case 'applications': return <BriefcaseIcon className="w-5 h-5" />;
            case 'interviews': return <Calendar className="w-5 h-5" />;
            case 'revenue': return <DollarSign className="w-5 h-5" />;
            case 'time_to_hire': return <Clock className="w-5 h-5" />;
            default: return <Target className="w-5 h-5" />;
        }
    };

    const getStatusColor = (goalStatus) => {
        switch (goalStatus) {
            case 'completed': return 'text-green-600 bg-green-100';
            case 'on_track': return 'text-blue-600 bg-blue-100';
            case 'urgent': return 'text-orange-600 bg-orange-100';
            case 'overdue': return 'text-red-600 bg-red-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'critical': return 'text-red-600 bg-red-100';
            case 'high': return 'text-orange-600 bg-orange-100';
            case 'medium': return 'text-blue-600 bg-blue-100';
            case 'low': return 'text-gray-600 bg-gray-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const filteredGoals = goals.filter(goal => {
        if (filter === 'all') return true;
        if (filter === 'active') return goal.status === 'active';
        if (filter === 'completed') return goal.status === 'completed';
        if (filter === 'urgent') return goal.goal_status === 'urgent' || goal.goal_status === 'overdue';
        return true;
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Goals</h1>
                    <p className="text-gray-600">Track and manage your recruitment goals</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Goal
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-sm border">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Target className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Goals</p>
                            <p className="text-2xl font-bold text-gray-900">{goalStats.total_goals || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm border">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Completed</p>
                            <p className="text-2xl font-bold text-gray-900">{goalStats.completed_goals || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm border">
                    <div className="flex items-center">
                        <div className="p-2 bg-orange-100 rounded-lg">
                            <AlertTriangle className="w-6 h-6 text-orange-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Overdue</p>
                            <p className="text-2xl font-bold text-gray-900">{goalStats.overdue_goals || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm border">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <TrendingUp className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Avg Progress</p>
                            <p className="text-2xl font-bold text-gray-900">{goalStats.avg_progress || 0}%</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b">
                    <div className="flex space-x-4">
                        {[
                            { key: 'all', label: 'All Goals' },
                            { key: 'active', label: 'Active' },
                            { key: 'completed', label: 'Completed' },
                            { key: 'urgent', label: 'Urgent' }
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setFilter(tab.key)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    filter === tab.key
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Goals List */}
                <div className="p-6">
                    {filteredGoals.length === 0 ? (
                        <div className="text-center py-8">
                            <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">No goals found</p>
                            <p className="text-sm text-gray-400">Create your first goal to start tracking progress</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredGoals.map(goal => (
                                <div key={goal.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <div className={`p-2 rounded-lg ${getPriorityColor(goal.priority)}`}>
                                                    {getGoalIcon(goal.goal_type)}
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-900">{goal.title}</h3>
                                                    <p className="text-sm text-gray-600">{goal.description}</p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                                <div>
                                                    <p className="text-xs text-gray-500">Progress</p>
                                                    <p className="text-sm font-medium">{goal.current_value} / {goal.target_value} {goal.unit}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">Completion</p>
                                                    <p className="text-sm font-medium">{goal.progress_percentage}%</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">Days Left</p>
                                                    <p className="text-sm font-medium">{goal.days_remaining} days</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">Status</p>
                                                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(goal.goal_status)}`}>
                                                        {goal.goal_status.replace('_', ' ')}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Progress Bar */}
                                            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                                                <div
                                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${Math.min(goal.progress_percentage, 100)}%` }}
                                                ></div>
                                            </div>

                                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                                                <span>Period: {goal.period_type}</span>
                                                <span>Priority: {goal.priority}</span>
                                                <span>End Date: {new Date(goal.end_date).toLocaleDateString()}</span>
                                            </div>
                                        </div>

                                        <div className="flex space-x-2 ml-4">
                                            {goal.auto_calculate && (
                                                <button
                                                    onClick={() => handleAutoCalculate(goal.id)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Auto-calculate progress"
                                                >
                                                    <BarChart3 className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    const newValue = prompt('Enter new progress value:', goal.current_value);
                                                    if (newValue !== null && !isNaN(newValue)) {
                                                        handleUpdateProgress(goal.id, parseFloat(newValue));
                                                    }
                                                }}
                                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                title="Update progress"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Goal Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Goal</h2>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Goal Type</label>
                                <select
                                    value={newGoal.goal_type}
                                    onChange={(e) => setNewGoal({...newGoal, goal_type: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="hires">Hires</option>
                                    <option value="applications">Applications</option>
                                    <option value="interviews">Interviews</option>
                                    <option value="revenue">Revenue</option>
                                    <option value="time_to_hire">Time to Hire</option>
                                    <option value="custom">Custom</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={newGoal.title}
                                    onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter goal title"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={newGoal.description}
                                    onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    rows="3"
                                    placeholder="Describe your goal"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Value</label>
                                    <input
                                        type="number"
                                        value={newGoal.target_value}
                                        onChange={(e) => setNewGoal({...newGoal, target_value: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                                    <input
                                        type="text"
                                        value={newGoal.unit}
                                        onChange={(e) => setNewGoal({...newGoal, unit: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="candidates, applications, etc."
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                                    <select
                                        value={newGoal.period_type}
                                        onChange={(e) => setNewGoal({...newGoal, period_type: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                        <option value="quarterly">Quarterly</option>
                                        <option value="yearly">Yearly</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                                    <select
                                        value={newGoal.priority}
                                        onChange={(e) => setNewGoal({...newGoal, priority: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="critical">Critical</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={newGoal.start_date}
                                        onChange={(e) => setNewGoal({...newGoal, start_date: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        value={newGoal.end_date}
                                        onChange={(e) => setNewGoal({...newGoal, end_date: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex space-x-3 mt-6">
                            <button
                                onClick={handleCreateGoal}
                                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Create Goal
                            </button>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoalsSection;
